import { ComponentType, PropsWithChildren, ReactNode } from 'react';

// eslint-disable-next-line @typescript-eslint/no-restricted-imports
import { Avatar, Link, Typography, makeStyles } from '@material-ui/core';

import { RiSearchLine as Search } from '@remixicon/react';
import classNames from 'classnames';

const useStyles = makeStyles(theme => ({
  container: {
    position: 'relative',
    textAlign: 'center',
    padding: theme.spacing(4),
    height: '100%',
  },
  content: {
    height: '100%',
    margin: '0 auto',
    gap: theme.spacing(1),
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    maxWidth: 500,
  },
  avatar: {
    height: 120,
    width: 120,
    marginBottom: theme.spacing(2),
  },
  icon: {
    width: 80,
    height: 80,
  },
  actions: {
    display: 'flex',
    marginTop: theme.spacing(2),
    alignItems: 'center',
    gap: theme.spacing(1),
  },
  contactSupportSnippet: {
    marginTop: theme.spacing(5),
    color: theme.palette.text.disabled,
  },
}));

export type ListEmptyStateProps = PropsWithChildren<{
  className?: string;
  title: string;
  description?: ReactNode;
  actions?: ReactNode;
  supportSnippet?: ReactNode;
  minimal?: boolean;
  icon?: ComponentType<{ className?: string }>;
}>;

export const ListEmptyState = ({
  className,
  title,
  description,
  actions,
  minimal,
  supportSnippet = (
    <span>
      Not the results you expected?{' '}
      <Link
        underline="always"
        href="mailto:confidence@spotify.com"
        target="_blank"
      >
        Contact support
      </Link>
      .
    </span>
  ),
  icon: Icon = Search,
  children,
}: ListEmptyStateProps) => {
  const classes = useStyles();

  return (
    <div className={classNames(classes.container, className)}>
      <div className={classes.content}>
        {!minimal && (
          <Avatar className={classes.avatar}>
            <Icon className={classes.icon} />
          </Avatar>
        )}
        <Typography variant={minimal ? 'body1' : 'h4'}>{title}</Typography>
        {description && (
          <Typography color="textSecondary" component="div">
            {description}
          </Typography>
        )}
        {actions && <div className={classes.actions}>{actions}</div>}
        {Boolean(supportSnippet) && !minimal && (
          <Typography
            className={classes.contactSupportSnippet}
            variant="caption"
            component="div"
          >
            {supportSnippet}
          </Typography>
        )}
      </div>
      {children}
    </div>
  );
};
